import {
  withCompInfo,
  createComponentMapperModel,
} from '@wix/editor-elements-integrations';
import {
  migrateFields,
  getLabelPaddingsValues,
  convertPhysicalInputAlignmentToDirection,
  addUnitToEveryField,
  getWidthInPixels,
  getHeightInPixels,
} from '@wix/editor-elements-common-utils';
import type {
  ToggleSwitchDefinition,
  IToggleSwitchMapperProps,
  ToggleSwitchRootCSS,
  ToggleSwitchStyleProperties,
} from '../ToggleSwitch.types';

function concatPercent(value: number): string {
  return value !== 0 ? `${value}%` : value.toString();
}

function isOuterKnob(knobSize: number, trackHeight: number): boolean {
  return knobSize > trackHeight;
}

function innerKnobWidth(
  knobSize: number,
  trackHeight: number,
  initialKnobWidth?: number,
): number {
  if (typeof initialKnobWidth !== 'undefined') {
    return 100 - initialKnobWidth - (trackHeight - knobSize) / 4;
  }
  return (trackHeight - knobSize) / 4;
}

function outerKnobWidth(initialKnobWidth: number): number {
  return 100 - initialKnobWidth;
}

function calcUncheckedLeftValue(knobSize: number, trackHeight: number): number {
  return isOuterKnob(knobSize, trackHeight)
    ? 0
    : innerKnobWidth(knobSize, trackHeight);
}

function calcCheckedLeftValue(
  knobSize: number,
  trackHeight: number,
  knobWidth: number,
): number {
  return isOuterKnob(knobSize, trackHeight)
    ? outerKnobWidth(knobWidth)
    : innerKnobWidth(knobSize, trackHeight, knobWidth);
}

export const props = withCompInfo<
  IToggleSwitchMapperProps,
  ToggleSwitchDefinition
>()(
  ['compData', 'compProps', 'hasResponsiveLayout'],
  ({ compData, compProps, hasResponsiveLayout }) => {
    const { checked, value } = compData;
    const { isDisabled, label } = compProps;
    return {
      hasResponsiveLayout,
      isDisabled,
      checked,
      value,
      label,
    };
  },
);

type Overrides = {
  styleProperties: ToggleSwitchStyleProperties;
};

export const css = withCompInfo<
  ToggleSwitchRootCSS,
  ToggleSwitchDefinition,
  never,
  Overrides
>()(
  [
    'compProps',
    'compLayout',
    'compSingleLayout',
    'hasResponsiveLayout',
    'styleProperties',
    'compData',
    'isOneDocMigrated',
  ],
  ({
    compProps,
    compLayout,
    compSingleLayout,
    hasResponsiveLayout,
    styleProperties,
    compData,
    isOneDocMigrated,
  }) => {
    const {
      displayKnobIcons,
      trackHeight,
      knobSize,
      label,
      labelMargin,
      switchHeight,
      labelPadding,
    } = compProps;
    const { direction } = compData;
    const { align = 'start' } = styleProperties;

    const knobWidth = knobSize / 2;

    const cssVars: ToggleSwitchRootCSS = {
      '--toggleIconDisplay': displayKnobIcons ? 'block' : 'none',
      '--innerLabelHeight': concatPercent(knobSize),
      '--innerLabelWidth': concatPercent(knobWidth),
      '--outerLabelHeight': concatPercent(trackHeight),
      '--labelMovementRangeChecked': concatPercent(
        calcCheckedLeftValue(knobSize, trackHeight, knobWidth),
      ),
      '--labelMovementRangeUnchecked': concatPercent(
        calcUncheckedLeftValue(knobSize, trackHeight),
      ),
      '--align': align,
      direction,
    };

    if (label) {
      const paddings = getLabelPaddingsValues({
        labelPadding,
        align,
      });

      return {
        ...cssVars,
        ...(hasResponsiveLayout ? {} : { height: 'auto' }),
        '--switchHeight': `${switchHeight}px`,
        '--switchWidth': `${switchHeight * 2}px`,
        '--labelMarginBottom': `${labelMargin}px`,
        ...addUnitToEveryField(paddings),
      };
    }

    const height = isOneDocMigrated
      ? getHeightInPixels(compSingleLayout)
      : compLayout.height;

    const width = isOneDocMigrated
      ? getWidthInPixels(compSingleLayout)
      : compLayout.width;
    const wrongAspectRatio = height && width ? height * 2 !== width : false;
    if (height && wrongAspectRatio && !hasResponsiveLayout) {
      return {
        ...cssVars,
        height: `${switchHeight || height}px`,
        width: `${(switchHeight || height) * 2}px`,
      };
    }

    return cssVars;
  },
  [
    migrateFields([
      {
        sourceNamespace: 'compProps',
        targetNamespace: 'compData',
        fields: [{ source: 'alignment', target: 'direction' }],
        enhancer: convertPhysicalInputAlignmentToDirection,
      },
    ]),
  ],
);

export default createComponentMapperModel({ css, props });
